import { useScrollReveal, useScrollRevealGroup } from '@/hooks/useScrollReveal';

export default function CompHero() {
  const { ref: badgeRef, revealed: badgeRevealed } = useScrollReveal({ delay: 100 });
  const { ref: headlineRef, revealed: headlineRevealed } = useScrollReveal({ delay: 250 });
  const { ref: subtitleRef, revealed: subtitleRevealed } = useScrollReveal({ delay: 400 });
  const { ref: btnsRef, revealed: btnsRevealed } = useScrollReveal({ delay: 550 });
  const { containerRef: statsRef, revealedCount: statsRevealed } = useScrollRevealGroup<HTMLDivElement>(4);

  return (
    <section className="relative w-full min-h-screen flex items-center bg-background-50 overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'radial-gradient(circle, oklch(var(--primary-500)) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />
      <div className="absolute top-20 right-0 w-[600px] h-[600px] opacity-[0.06]"
        style={{ background: 'radial-gradient(circle at center, oklch(var(--primary-500)), transparent 70%)' }}
      />
      <div className="absolute bottom-10 left-0 w-[500px] h-[500px] opacity-[0.04]"
        style={{ background: 'radial-gradient(circle at center, oklch(var(--primary-500)), transparent 70%)' }}
      />

      {/* Tech-ноды */}
      <div className="absolute top-1/3 right-[12%] hidden lg:block">
        <div className="relative w-20 h-20">
          <div className="absolute inset-0 rounded-full border border-primary-500/20 animate-ping" style={{ animationDuration: '3s' }} />
          <div className="absolute inset-2 rounded-full border border-primary-500/30" />
          <div className="absolute inset-[30%] rounded-full bg-primary-500/10 flex items-center justify-center">
            <i className="ri-crosshair-line text-primary-500 text-lg"></i>
          </div>
        </div>
      </div>
      <div className="absolute bottom-1/4 left-[8%] hidden lg:block">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-full border border-primary-500/15 animate-ping" style={{ animationDuration: '4s', animationDelay: '1s' }} />
          <div className="absolute inset-[25%] rounded-full bg-primary-500/5 flex items-center justify-center">
            <i className="ri-radar-line text-primary-400 text-base"></i>
          </div>
        </div>
      </div>
      <div className="absolute top-1/2 right-[5%] hidden xl:block w-px h-32 bg-gradient-to-b from-transparent via-primary-300/30 to-transparent" />
      <div className="absolute bottom-1/3 left-[18%] hidden xl:block w-24 h-px bg-gradient-to-r from-transparent via-primary-300/20 to-transparent" />

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 pt-28 pb-12 flex flex-col items-start">
        <div ref={badgeRef} className={`inline-flex items-center gap-2 px-4 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200/60 rounded-full mb-8 reveal-fade-up ${badgeRevealed ? 'revealed' : ''}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
          Продукт AIGAM
        </div>

        <h1 ref={headlineRef} className={`font-display font-extrabold text-[44px] md:text-[64px] leading-[1.05] tracking-tightest text-foreground-950 max-w-3xl reveal-fade-up ${headlineRevealed ? 'revealed' : ''}`}>
          Заберите клиентов
          <br />
          у конкурентов
          <br />
          раньше них
        </h1>

        <p ref={subtitleRef} className={`mt-6 md:mt-8 text-[17px] md:text-[19px] text-foreground-500 max-w-xl leading-relaxed reveal-fade-up ${subtitleRevealed ? 'revealed' : ''}`}>
          AIGAM находит пользователей, которые уже взаимодействуют с вашими конкурентами и находятся в стадии покупки — и запускает AI-контакт быстрее, чем они успевают закрыть сделку.
        </p>

        <div ref={btnsRef} className={`mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3 reveal-fade-up ${btnsRevealed ? 'revealed' : ''}`}>
          <a
            href="#comp-pricing"
            className="h-12 px-7 inline-flex items-center justify-center rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-[15px] font-semibold transition-colors whitespace-nowrap cursor-pointer"
          >
            Начать перехват
          </a>
          <a
            href="#comp-how"
            className="h-12 px-6 inline-flex items-center justify-center rounded-md border border-background-200 hover:border-background-300 hover:bg-background-100 text-foreground-700 text-[15px] font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            Как это работает
          </a>
        </div>

        {/* Статистика */}
        <div ref={statsRef} className="mt-16 md:mt-20 w-full max-w-3xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-0 md:divide-x divide-background-200/70">
            {[
              { value: '500 000+', label: 'Профилей отслежено', icon: 'ri-user-search-line' },
              { value: '50+', label: 'Ниш для анализа', icon: 'ri-stack-line' },
              { value: '×4.2', label: 'Рост сделок', icon: 'ri-arrow-up-circle-line' },
              { value: '< 24 ч', label: 'До первого контакта', icon: 'ri-speed-up-line' },
            ].map((stat, i) => (
              <div key={stat.label} className={`text-center px-4 py-3 reveal-fade-up ${i < statsRevealed ? 'revealed' : ''}`} style={i < statsRevealed ? { transitionDelay: `${i * 100}ms` } : undefined}>
                <span className="w-8 h-8 mx-auto mb-2 rounded-md bg-primary-100 flex items-center justify-center text-primary-600">
                  <i className={`${stat.icon} text-sm`}></i>
                </span>
                <div className="text-[20px] font-display font-extrabold text-foreground-950">
                  {stat.value}
                </div>
                <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}